// Color palettes for the percentile-pair d10s in DiceRollTumble.
//
// Each palette names the two dice (tens / ones) by purpose, not by index,
// so future palette additions read cleanly. To add a palette:
//   1) drop a new entry into PALETTES below;
//   2) it auto-appears in the AtmosphereSettingsPanel selector;
//   3) no code change needed in DiceRollTumble itself.
//
// Round 9 (2026-06-05): user explicitly asked for 3 named palettes
// selectable from settings, plus "more in the future". The shape
// here is the extension surface.

export type DicePaletteId =
  | 'bond'
  | 'navy'
  | 'casino'
  | 'midnight'
  | 'kremlin'
  | 'goldfinger'
  | 'scaramanga'
  | 'spectre'
  | 'moonraker';

export interface DicePalette {
  id: DicePaletteId;
  label: string;
  /** Color applied to the TENS d10 (00, 10, …, 90). */
  tensColor: string;
  /** Color applied to the ONES d10 (0, 1, …, 9). */
  onesColor: string;
}

export const DICE_PALETTES: Record<DicePaletteId, DicePalette> = {
  bond: {
    id: 'bond',
    label: 'Bond — gold tens · white ones',
    tensColor: '#c9a14a',
    onesColor: '#ffffff',
  },
  navy: {
    id: 'navy',
    label: 'Navy — gold tens · deep navy ones',
    tensColor: '#c9a14a',
    onesColor: '#0d1e3a',
  },
  casino: {
    id: 'casino',
    label: 'Casino — red tens · black ones',
    tensColor: '#b9202c',
    onesColor: '#1a1a1a',
  },
  // Round 14 (2026-06-06): Sprint 1 — 6 additional palettes per the
  // §4.1 detailed plan. Color-theory considerations applied: tens die
  // gets the warmer/more saturated hue (reinforces "higher value"
  // intuition); ones die stays above ~#2c on the luminance axis to
  // remain readable against the black dice scene; red/green pairs
  // avoided (protanopia concern). bond/navy/casino remain.
  midnight: {
    id: 'midnight',
    label: 'Midnight — ice-blue tens · charcoal ones',
    tensColor: '#7ec8e3',
    onesColor: '#2c2c2c',
  },
  kremlin: {
    id: 'kremlin',
    label: 'Kremlin — crimson tens · gunmetal ones',
    tensColor: '#cc2200',
    onesColor: '#4a4a4a',
  },
  goldfinger: {
    id: 'goldfinger',
    label: 'Goldfinger — deep gold tens · ebony ones',
    tensColor: '#d4af37',
    onesColor: '#1c1c1c',
  },
  scaramanga: {
    id: 'scaramanga',
    label: 'Scaramanga — amber tens · ivory ones',
    tensColor: '#c07028',
    onesColor: '#f0e6cc',
  },
  spectre: {
    id: 'spectre',
    label: 'SPECTRE — violet tens · slate ones',
    tensColor: '#7b3f9e',
    onesColor: '#556b7d',
  },
  moonraker: {
    id: 'moonraker',
    label: 'Moonraker — silver tens · pearl ones',
    tensColor: '#c0c0c0',
    onesColor: '#f5f5f0',
  },
};

export const DEFAULT_DICE_PALETTE: DicePaletteId = 'bond';

/** Public: deterministic palette ordering for UI selectors. */
export const DICE_PALETTE_OPTIONS: DicePalette[] = (Object.keys(DICE_PALETTES) as DicePaletteId[]).map(
  (id) => DICE_PALETTES[id],
);
